import { useMemo, useState } from "react";

/**
 * A reusable, sortable data table with dark-mode styling
 * and animated row highlighting.
 *
 * @param {Object} props
 * @param {Array<Object>} props.columns - Column definitions.
 * @param {Array<Object>} props.data - Table data.
 *
 * @param {Object} [props.highlight]
 * Optional rule for highlighting matching rows.
 *
 * @param {string} props.highlight.key
 * Property name to match against each row.
 *
 * @param {any} props.highlight.value
 * Value to match using strict equality.
 *
 * @param {string} [props.highlight.color="#f9a928"]
 * CSS color used for the highlight.
 *
 * @example
 * <SortableTable
 *   columns={columns}
 *   data={data}
 *   highlight={{
 *     key: "status",
 *     value: "pending",
 *     color: "#f9a928",
 *   }}
 * />
 */

/**
 * Converts a hex color into an RGB string.
 * Supports 3-digit and 6-digit hex colors.
 */
function hexToRgb(hex) {
  if (typeof hex !== "string") return null;

  const value = hex.replace(/^#/, "");

  const fullHex =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) {
    return null;
  }

  return [
    parseInt(fullHex.slice(0, 2), 16),
    parseInt(fullHex.slice(2, 4), 16),
    parseInt(fullHex.slice(4, 6), 16),
  ].join(", ");
}

/**
 * Converts a CSS color to a translucent CSS color.
 * Supports hex, rgb(), and named CSS colors through
 * color-mix(), without requiring color parsing.
 */
function translucentColor(color, opacity) {
  return `color-mix(in srgb, ${color} ${opacity * 100}%, transparent)`;
}

export default function SortableTable({
  columns,
  data,
  highlight,
}) {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const highlightColor = highlight?.color ?? "#f9a928";

  const highlightRgb = hexToRgb(highlightColor);

  /**
   * Returns the value used when sorting a row.
   */
  const getSortValue = (column, row) => {
    const value = row[column.key];

    return column.sortValue
      ? column.sortValue(value, row)
      : value;
  };

  /**
   * Sort table data without modifying the original array.
   */
  const sortedData = useMemo(() => {
    if (sortConfig.key == null) {
      return data;
    }

    const column = columns.find(
      (column) => column.key === sortConfig.key
    );

    if (!column) {
      return data;
    }

    return [...data].sort((a, b) => {
      const aValue = getSortValue(column, a);
      const bValue = getSortValue(column, b);

      // Keep null and undefined values at the bottom.
      if (aValue == null && bValue == null) {
        return 0;
      }

      if (aValue == null) {
        return 1;
      }

      if (bValue == null) {
        return -1;
      }

      let comparison;

      if (
        typeof aValue === "number" &&
        typeof bValue === "number"
      ) {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(
          String(bValue),
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          }
        );
      }

      return sortConfig.direction === "asc"
        ? comparison
        : -comparison;
    });
  }, [data, columns, sortConfig]);

  /**
   * Handles sorting when a column header is clicked.
   */
  const handleSort = (column) => {
    if (column.key == null) {
      return;
    }

    setSortConfig((current) => ({
      key: column.key,
      direction:
        current.key === column.key &&
        current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  /**
   * Renders the contents of a table cell.
   */
  const renderCell = (column, row) => {
    const value =
      column.key == null
        ? undefined
        : row[column.key];

    return column.render
      ? column.render(value, row)
      : value;
  };

  /**
   * Determines whether a row matches the highlight rule.
   */
  const isRowHighlighted = (row) => {
    if (!highlight || highlight.key == null) {
      return false;
    }

    return row[highlight.key] === highlight.value;
  };

  /**
   * CSS variables for highlight colors.
   * These allow smooth color transitions without
   * requiring dynamically generated Tailwind classes.
   */
  const highlightStyles = {
    "--highlight-color": highlightColor,

    "--highlight-bg": translucentColor(
      highlightColor,
      0.13
    ),

    "--highlight-hover": translucentColor(
      highlightColor,
      0.21
    ),

    "--highlight-border": translucentColor(
      highlightColor,
      0.4
    ),

    "--highlight-glow": translucentColor(
      highlightColor,
      0.35
    ),

    ...(highlightRgb && {
      "--highlight-rgb": highlightRgb,
    }),
  };

  return (
    <table
      style={highlightStyles}
      className="
        min-w-full
        table-fixed
        border-separate
        border-spacing-0
        rounded-lg
        border
        border-neutral-800
        shadow-lg
      "
    >
      <thead>
        <tr>
          {columns.map((column, index) => {
            const isSortable = column.key != null;
            const isSorted =
              isSortable &&
              sortConfig.key === column.key;

            const isFirstColumn = index === 0;
            const isLastColumn =
              index === columns.length - 1;

            return (
              <th
                key={column.key ?? `column-${index}`}
                scope="col"
                onClick={() => handleSort(column)}
                style={{
                  width: column.width,
                }}
                className={[
                  "bg-[#444]",
                  "px-2 py-4",
                  "text-left text-xs font-semibold",
                  "uppercase tracking-wider text-gray-200",
                  "transition-colors duration-200",

                  isFirstColumn
                    ? "rounded-tl-lg"
                    : "",

                  isLastColumn
                    ? "rounded-tr-lg"
                    : "",

                  isSortable
                    ? "cursor-pointer select-none hover:bg-[#4d4d4d]"
                    : "",
                ].join(" ")}
              >
                <div className="flex items-center gap-2">
                  {column.label}

                  {isSortable && (
                    <span className="text-gray-400">
                      {isSorted
                        ? sortConfig.direction === "asc"
                          ? "↑"
                          : "↓"
                        : "↕"}
                    </span>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>

      <tbody className="divide-y divide-gray-700">
        {sortedData.map((row, rowIndex) => {
          const isLastRow =
            rowIndex === sortedData.length - 1;

          const isHighlighted =
            isRowHighlighted(row);

          return (
            <tr
              key={row.id ?? rowIndex}
              className="group"
            >
              {columns.map(
                (column, columnIndex) => {
                  const isFirstColumn =
                    columnIndex === 0;

                  const isLastColumn =
                    columnIndex ===
                    columns.length - 1;

                  return (
                    <td
                      key={
                        column.key ??
                        `column-${columnIndex}`
                      }
                      style={{
                        backgroundColor: isHighlighted
                          ? "var(--highlight-bg)"
                          : "#333333",

                        boxShadow: isHighlighted
                          ? `
                            inset 0 1px 0 var(--highlight-border),
                            inset 0 -1px 0 var(--highlight-border)
                          `
                          : "none",
                      }}
                      className={[
                        "relative",
                        "px-2 py-2",
                        "text-sm text-gray-200",

                        // Smooth background and glow transitions.
                        "transition-[background-color,box-shadow]",
                        "duration-300 ease-in-out",

                        // Hover effects.
                        isHighlighted
                          ? "group-hover:!bg-[var(--highlight-hover)]"
                          : "group-hover:!bg-neutral-800",

                        // Bottom corner rounding.
                        isLastRow && isFirstColumn
                          ? "rounded-bl-lg"
                          : "",

                        isLastRow && isLastColumn
                          ? "rounded-br-lg"
                          : "",
                      ].join(" ")}
                    >
                      {/* Animated left-edge accent */}
                      {isFirstColumn && (
                        <span
                          aria-hidden="true"
                          style={{
                            backgroundColor:
                              "var(--highlight-color)",

                            boxShadow: isHighlighted
                              ? "0 0 12px var(--highlight-glow)"
                              : "none",
                          }}
                          className={[
                            "pointer-events-none",
                            "absolute left-0 top-0 bottom-0",
                            "w-[3px]",
                            "origin-center",

                            // Smooth entrance/exit animation.
                            "transition-[opacity,transform,box-shadow,background-color]",
                            "duration-500 ease-out",

                            isHighlighted
                              ? "opacity-100 scale-y-100"
                              : "opacity-0 scale-y-50",
                          ].join(" ")}
                        />
                      )}

                      {renderCell(column, row)}
                    </td>
                  );
                }
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
